import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Upload, Trash2, Loader2 } from "lucide-react";
import { AvatarCropperDialog } from "@/components/account/avatar-cropper-dialog";
import { getPresetAvatars } from "@/components/account/preset-avatars";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { logUploadFailure, type UploadFailureReason } from "@/lib/upload-audit";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

// Upload a blob to Supabase Storage using XHR so we can report real progress.
function xhrUpload(
  url: string,
  token: string,
  apikey: string,
  blob: Blob,
  contentType: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("apikey", apikey);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.setRequestHeader("x-upsert", "true");
    xhr.setRequestHeader("cache-control", "3600");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else {
        try {
          const msg = JSON.parse(xhr.responseText)?.message;
          reject(new Error(msg || `Upload failed (${xhr.status})`));
        } catch {
          reject(new Error(`Upload failed (${xhr.status})`));
        }
      }
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.onabort = () => reject(new Error("Upload cancelled"));
    xhr.send(blob);
  });
}

export const Route = createFileRoute("/_authenticated/_account/profile")({
  head: () => ({
    meta: [
      { title: "Profile — CoderLity" },
      { name: "description", content: "Manage your CoderLity profile." },
    ],
  }),
  component: ProfilePage,
});

type ProfileRow = {
  display_name: string | null;
  avatar_url: string | null;
  avatar_circle_url: string | null;
  bio: string | null;
  website: string | null;
  phone: string | null;
};

const empty: ProfileRow = {
  display_name: "",
  avatar_url: "",
  avatar_circle_url: "",
  bio: "",
  website: "",
  phone: "",
};

function ProfilePage() {
  const { user } = useAuth();
  const [form, setForm] = useState<ProfileRow>(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropMime, setCropMime] = useState<string | null>(null);

  const initials = (form.display_name || user?.email || "?").slice(0, 2).toUpperCase();
  const presets = getPresetAvatars(form.display_name || user?.email || "U");
  const defaultAvatar = presets[0]?.url ?? "";
  const displayedAvatar = form.avatar_url || defaultAvatar;
  const hasCustomAvatar = !!form.avatar_url;

  const onPickFile = () => fileInputRef.current?.click();

  // Remove a previous storage object if it belongs to the avatars bucket.
  // Cleanup failures are logged but never block the user — the new avatar
  // is already saved on the profile; an orphaned previous file is recoverable.
  const cleanupPrevious = async (prevUrl: string | null | undefined) => {
    if (!prevUrl) return;
    const marker = "/storage/v1/object/public/avatars/";
    const idx = prevUrl.indexOf(marker);
    if (idx === -1) return;
    let path: string;
    try {
      path = decodeURIComponent(prevUrl.slice(idx + marker.length));
    } catch (err) {
      void logUploadFailure({
        userId: user?.id,
        bucket: "avatars",
        reason: "cleanup_failed",
        message: "Could not decode previous avatar path",
        extra: {
          source: "profile_avatar_cleanup",
          prev_url: prevUrl,
          error: err instanceof Error ? err.message : String(err),
        },
      });
      return;
    }

    try {
      const { data, error } = await supabase.storage.from("avatars").remove([path]);
      if (error) {
        void logUploadFailure({
          userId: user?.id,
          bucket: "avatars",
          reason: "cleanup_failed",
          message: error.message,
          extra: { source: "profile_avatar_cleanup", path, prev_url: prevUrl },
        });
        // Soft, non-blocking signal — the new avatar is already live.
        toast.warning("Old profile picture couldn't be removed", {
          description: "It may stay in storage until cleaned up later.",
        });
        return;
      }
      // Storage may return success with an empty array (object not found / RLS hides it).
      if (Array.isArray(data) && data.length === 0) {
        void logUploadFailure({
          userId: user?.id,
          bucket: "avatars",
          reason: "cleanup_failed",
          message: "Remove returned no results (file missing or not permitted)",
          extra: { source: "profile_avatar_cleanup", path, prev_url: prevUrl },
        });
      }
    } catch (err) {
      void logUploadFailure({
        userId: user?.id,
        bucket: "avatars",
        reason: "cleanup_failed",
        message: err instanceof Error ? err.message : "Unknown cleanup error",
        extra: { source: "profile_avatar_cleanup", path, prev_url: prevUrl },
      });
      toast.warning("Old profile picture couldn't be removed", {
        description: "It may stay in storage until cleaned up later.",
      });
    }
  };

  const ALLOWED_MIME = ["image/png", "image/jpeg", "image/webp", "image/gif"] as const;
  const ALLOWED_EXT = ["png", "jpg", "jpeg", "webp", "gif"] as const;
  const MAX_SIZE = 5 * 1024 * 1024; // 5MB

  const auditAvatar = (
    reason: UploadFailureReason,
    message: string,
    file?: File | { name?: string; size?: number; type?: string },
  ) =>
    logUploadFailure({
      userId: user?.id,
      bucket: "avatars",
      reason,
      message,
      file: file
        ? { name: file.name, size: file.size, type: (file as File).type }
        : undefined,
      extra: { source: "profile_avatar" },
    });

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user) return;

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!(ALLOWED_MIME as readonly string[]).includes(file.type)) {
      const msg = "Unsupported format. Use PNG, JPG, WEBP or GIF.";
      void auditAvatar("disallowed_mime", msg, file);
      return toast.error(msg);
    }
    if (!(ALLOWED_EXT as readonly string[]).includes(ext)) {
      const msg = "Unsupported file extension.";
      void auditAvatar("disallowed_extension", msg, file);
      return toast.error(msg);
    }
    if (file.size === 0) {
      void auditAvatar("empty_file", "File is empty", file);
      return toast.error("File is empty");
    }
    if (file.size > MAX_SIZE) {
      void auditAvatar("too_large", "Image must be under 5MB", file);
      return toast.error("Image must be under 5MB");
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string" || !result.startsWith("data:")) {
        void auditAvatar("read_error", "Decoded result was not a data URL", file);
        toast.error("Couldn't read this image", {
          description: "The file may be corrupted or in an unsupported format. Try another one.",
        });
        return;
      }
      setCropSrc(result);
      setCropMime(file.type || null);
    };
    reader.onerror = () => {
      void auditAvatar("read_error", "FileReader failed", file);
      toast.error("Couldn't read this image", {
        description: "Your browser was unable to open the file. Try a different image.",
      });
    };
    reader.onabort = () => {
      toast.error("Image read was cancelled");
    };
    reader.readAsDataURL(file);
  };

  const uploadCropped = async (result: { square: Blob; circle?: Blob }) => {
    if (!user) return;
    const { square, circle } = result;

    for (const [label, blob] of [
      ["square", square] as const,
      ...(circle ? [["circle", circle] as const] : []),
    ]) {
      if (blob.size > MAX_SIZE) {
        void auditAvatar("too_large", `Cropped ${label} image is too large`, {
          name: `cropped-${label}`,
          size: blob.size,
          type: blob.type,
        });
        return toast.error(`Cropped ${label} image is too large`);
      }
    }

    setUploading(true);
    setProgress(0);
    const toastId = toast.loading("Uploading profile picture… 0%");
    const prevSquare = form.avatar_url;
    const prevCircle = form.avatar_circle_url;
    const extByMime: Record<string, string> = {
      "image/webp": "webp",
      "image/png": "png",
      "image/jpeg": "jpg",
    };
    const stamp = Date.now();
    const squareExt = extByMime[square.type] ?? "jpg";
    const squarePath = `${user.id}/avatar-${stamp}.${squareExt}`;
    const circlePath = circle ? `${user.id}/avatar-${stamp}-circle.png` : null;

    const uploadOne = async (
      token: string,
      apikey: string,
      blob: Blob,
      path: string,
      contentType: string,
      label: string,
      base: number,
      span: number,
    ) =>
      xhrUpload(
        `${SUPABASE_URL}/storage/v1/object/avatars/${path}`,
        token,
        apikey,
        blob,
        contentType,
        (pct) => {
          const overall = Math.round(base + (pct * span) / 100);
          setProgress(overall);
          toast.loading(`Uploading ${label}… ${overall}%`, { id: toastId });
        },
      );

    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error("You must be signed in");
      const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

      // Split the progress bar between the two uploads when both are present.
      const squareSpan = circle ? 50 : 100;
      await uploadOne(token, apikey, square, squarePath, square.type || "image/jpeg", "square", 0, squareSpan);
      let circleUrl: string | null = null;
      if (circle && circlePath) {
        await uploadOne(token, apikey, circle, circlePath, "image/png", "circle", 50, 50);
        circleUrl = supabase.storage.from("avatars").getPublicUrl(circlePath).data.publicUrl;
      }

      toast.loading("Saving…", { id: toastId });
      const squareUrl = supabase.storage.from("avatars").getPublicUrl(squarePath).data.publicUrl;
      const { error: updErr } = await supabase
        .from("profiles")
        .update({ avatar_url: squareUrl, avatar_circle_url: circleUrl })
        .eq("id", user.id);
      if (updErr) throw new Error(updErr.message);
      await cleanupPrevious(prevSquare);
      await cleanupPrevious(prevCircle);
      setForm((f) => ({ ...f, avatar_url: squareUrl, avatar_circle_url: circleUrl ?? "" }));
      setCropSrc(null);
      setCropMime(null);
      toast.success(
        circle ? "Profile picture updated (square + circle)" : "Profile picture updated",
        { id: toastId },
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      toast.error(msg, { id: toastId, description: "Please try again." });
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("display_name, avatar_url, avatar_circle_url, bio, website, phone")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setForm({ ...empty, ...data });
        setLoading(false);
      });
  }, [user]);

  const update = (k: keyof ProfileRow) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        display_name: form.display_name || null,
        avatar_url: form.avatar_url || null,
        bio: form.bio || null,
        website: form.website || null,
        phone: form.phone || null,
      })
      .eq("id", user.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Profile updated");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>How others see you across CoderLity.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-5">
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src={displayedAvatar || undefined} alt="Profile picture" />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={onFileChange}
              />
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={onPickFile} disabled={uploading || loading}>
                  {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {uploading ? "Uploading..." : hasCustomAvatar ? "Replace picture" : "Upload picture"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={uploading || loading || !hasCustomAvatar}
                  onClick={async () => {
                    if (!user) return;
                    if (!window.confirm("Remove your profile picture? You'll go back to the default avatar.")) {
                      return;
                    }
                    setUploading(true);
                    const toastId = toast.loading("Removing profile picture…");
                    const prev = form.avatar_url;
                    const prevCircle = form.avatar_circle_url;
                    const { error } = await supabase
                      .from("profiles")
                      .update({ avatar_url: null, avatar_circle_url: null })
                      .eq("id", user.id);
                    if (error) {
                      setUploading(false);
                      return toast.error(error.message, { id: toastId });
                    }
                    await cleanupPrevious(prev);
                    await cleanupPrevious(prevCircle);
                    setUploading(false);
                    setForm((f) => ({ ...f, avatar_url: "", avatar_circle_url: "" }));
                    toast.success("Profile picture removed", { id: toastId });
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove photo
                </Button>
              </div>
              {uploading && progress > 0 && (
                <div className="space-y-1">
                  <Progress value={progress} className="h-2" />
                  <p className="text-xs text-muted-foreground">Uploading… {progress}%</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                PNG, JPG or GIF. Max 5MB. Deleting reverts to the default avatar.
              </p>
            </div>
          </div>

          <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
            <div>
              <div className="text-sm font-medium">Or pick a default</div>
              <p className="text-xs text-muted-foreground">No upload needed — choose one below.</p>
            </div>
            <div className="grid grid-cols-6 gap-2 sm:grid-cols-8">
              {presets.map((p) => {
                const active = form.avatar_url === p.url;
                return (
                  <button
                    key={p.id}
                    type="button"
                    aria-label={`Use ${p.label} avatar`}
                    disabled={uploading || loading}
                    onClick={async () => {
                      if (!user) return;
                      setUploading(true);
                      const toastId = toast.loading("Applying avatar…");
                      const prev = form.avatar_url;
                      const prevCircle = form.avatar_circle_url;
                      const { error } = await supabase
                        .from("profiles")
                        .update({ avatar_url: p.url, avatar_circle_url: null })
                        .eq("id", user.id);
                      if (error) {
                        setUploading(false);
                        return toast.error(error.message, { id: toastId });
                      }
                      await cleanupPrevious(prev);
                      await cleanupPrevious(prevCircle);
                      setUploading(false);
                      setForm((f) => ({ ...f, avatar_url: p.url, avatar_circle_url: "" }));
                      toast.success("Profile picture updated", { id: toastId });
                    }}
                    className={cn(
                      "aspect-square overflow-hidden rounded-full border-2 transition",
                      active ? "border-primary ring-2 ring-primary/30" : "border-transparent hover:border-border"
                    )}
                  >
                    <img src={p.url} alt="" className="h-full w-full object-cover" />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="display_name">Display name</Label>
              <Input id="display_name" value={form.display_name ?? ""} onChange={update("display_name")} disabled={loading} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" value={user?.email ?? ""} disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" type="tel" value={form.phone ?? ""} onChange={update("phone")} disabled={loading} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input id="website" type="url" placeholder="https://" value={form.website ?? ""} onChange={update("website")} disabled={loading} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="avatar_url">Avatar URL</Label>
              <Input id="avatar_url" type="url" placeholder="https://..." value={form.avatar_url ?? ""} onChange={update("avatar_url")} disabled={loading} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea id="bio" rows={4} value={form.bio ?? ""} onChange={update("bio")} disabled={loading} />
            </div>
          </div>
          <Button type="submit" disabled={saving || loading}>
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </form>
      </CardContent>
      <AvatarCropperDialog
        open={!!cropSrc}
        imageSrc={cropSrc}
        originalMime={cropMime}
        busy={uploading}
        onCancel={() => {
          setCropSrc(null);
          setCropMime(null);
        }}
        onCropped={uploadCropped}
      />
    </Card>
  );
}
